import { Component, ElementRef, HostListener, afterNextRender, effect, inject, input, viewChild } from '@angular/core';
import { axisBottom, axisLeft, extent, line, pointer, ScaleBand, scaleBand, ScaleLinear, scaleLinear, scaleTime, select, Selection, timeFormat } from 'd3';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-bar-linechart',
  standalone: true,
  templateUrl: './bar-linechart.component.html',
  styleUrls: ['./bar-linechart.component.scss'],
})
export class BarLineChart {
  private readonly transloco = inject(TranslocoService);

  private chartContainer = viewChild<ElementRef<HTMLElement>>('chartLineBar');

  public data = input.required<Array<any>>();
  private color = {
    idle: '#F4BC2B',
    moving: '#50AD41',
    globaltrend: '#3873C9',
  }
  public margin = input<{ top: number; right: number; bottom: number; left: number, height: number }>({ top: 27, right: 1, bottom: 47, left: 37, height: 182 });
  public viewMode = input<'all' | 'moving' | 'idle'>('all');

  width = 0;
  height = 0;

  constructor() {
    afterNextRender(() => {
      const container = this.chartContainer();
      const _data = this.data();
      const _margin = this.margin();
      const _viewMode = this.viewMode();
      if (container && container.nativeElement && _data && _margin && _viewMode) {
        this.createChart(container.nativeElement, _data, _margin, _viewMode);
      }
    })
    effect(() => {
      const container = this.chartContainer();
      const _data = this.data();
      const _margin = this.margin();
      const _viewMode = this.viewMode();
      if (container && container.nativeElement && _data && _margin && _viewMode) {
        this.createChart(container.nativeElement, _data, _margin, _viewMode);
      }
    });
  }

  @HostListener('window:resize') onResize() {
    const container = this.chartContainer();
    const _data = this.data();
    const _margin = this.margin();
    const _viewMode = this.viewMode();
    if (container && container.nativeElement && _data && _margin && _viewMode) {
      this.createChart(container.nativeElement, _data, _margin, _viewMode);
    }
  }

  createChart(
    element: HTMLElement,
    data: Array<any>, margin:
      { top: number; right: number; bottom: number; left: number, height: number },
    viewMode: 'all' | 'moving' | 'idle'
  ): void {

    if (!element || !element.getBoundingClientRect) return;

    const boundingRect = element.getBoundingClientRect();
    this.width = boundingRect.width - margin.left - margin.right;
    this.height = margin.height - margin.top - margin.bottom + 6;


    select(element).selectAll('svg').remove();
    select(element).selectAll('div.tooltip').remove();

    const svg = select(element)
      .append('svg')
      .attr('width', '100%')
      .attr('height', this.height + margin.top + margin.bottom)
      // .attr('viewBox', `0 0 ${this.width + margin.left + margin.right} ${this.height + margin.top + margin.bottom + 40}`)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);


    const yMax = data.reduce((max, d) => Math.max(max, d.idle + d.moving), 0);
    // const y = scaleLinear().domain([0, 100, 100, yMax]).range([this.height, 100, 93, 0]);
    const y = scaleLinear().domain([0, yMax]).range([this.height, 0]);
    svg.append('g')
      .call(axisLeft(y).ticks(6).tickFormat((d: any) => (d === 0 ? '0ℓ' : `${d}hr`)))
      .call((g) => {
        g.select('.domain').remove();
        g.selectAll('line')
          .attr('x2', this.width)
          .attr('stroke', '#D9D9D9');
        g.selectAll('text')
          .style('fill', '#9FA1A4')
          .style('font-family', 'nunito_sans')
          .style('font-size', '10px')
          .attr('text-anchor', 'middle')
          .attr('dx', '-10');
      });

    const chartContainer = svg.append('g').attr('class', 'chart');
    const barContainer = chartContainer.append('g').attr('class', 'bars');

    const x = scaleBand()
      .domain(data.map((d) => d.week))
      .range([0, this.width])

    const _extent = extent(data, (d, i) => i);
    const xLine = scaleLinear()
      .domain(_extent[0] !== undefined && _extent[1] !== undefined ? _extent : [0, 0])
      .range([0, this.width]);

    chartContainer.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${this.height})`)
      .call(axisBottom(x))
      .call((g) => {
        g.selectAll('line').remove();
        g.select('.domain').remove();
        g.selectAll('g').each((d, i, nodes) => {
          const tick = select(nodes[i]);
          if (i !== 0 && i !== nodes.length - 1) {
            const gap = (this.width - (36 * 2) - ((nodes.length - 2) * 36)) / (nodes.length - 1);
            const space = (i * 36) + (i * gap) + 18;
            tick.attr('transform', `translate(${space},0)`)
          }
        })
        g.selectAll('text')
          .each((d, i, nodes) => {
            const text = select(nodes[i]);
            if (i === 0) {
              // First tick: align left
              text.attr('text-anchor', 'start')
                .attr('x', -x.bandwidth() / 2);
            } else if (i === nodes.length - 1) {
              // Last tick: align right
              text.attr('text-anchor', 'end')
                .attr('x', x.bandwidth() / 2);
            } else {
              // Middle ticks: center
              text.attr('text-anchor', 'middle')
                .attr('x', 0);
            }
          })
          .style('fill', '#9FA1A4')
          .style('font-family', 'nunito_sans')
          .style('font-size', '10px');
      });

    // idle bars
    if (viewMode === 'all' || viewMode === 'idle') {
      barContainer.selectAll('.bar-idle')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'bar-idle')
        .attr('x', (d, i, nodes) => {
          const bandX = x(d.week)!;
          if (i === 0) {
            // align to left edge
            return bandX;
          } else if (i === data.length - 1) {
            // align to right edge by subtracting bar width
            return bandX + x.bandwidth() - 36;
          } else {
            // center in band
            const gap = (this.width - (36 * 2) - ((nodes.length - 2) * 36)) / (nodes.length - 1);
            const space = (i * 36) + (i * gap) + 18;
            return space - 18;
          }
        })
        // .attr('x', (d) => x(d.week)!)
        .attr('y', (d) => y(d.idle))
        .attr('width', 36)
        // .attr('width', x.bandwidth())
        .attr('height', (d) => this.height - y(d.idle))
        .attr('fill', this.color.idle)
        .style('stroke', 'none');
    }

    // moving bars
    if (viewMode === 'all' || viewMode === 'moving') {
      barContainer.selectAll('.bar-moving')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'bar-moving')
        .attr('x', (d, i, nodes) => {
          const bandX = x(d.week)!;
          if (i === 0) {
            // align to left edge
            return bandX;
          } else if (i === data.length - 1) {
            // align to right edge by subtracting bar width
            return bandX + x.bandwidth() - 36;
          } else {
            // center in band
            const gap = (this.width - (36 * 2) - ((nodes.length - 2) * 36)) / (nodes.length - 1);
            const space = (i * 36) + (i * gap) + 18;
            return space - 18;
          }
        })
        // .attr('x', (d) => { return x(d.week)! })
        .attr('y', (d) => {
          const idle = viewMode === 'moving' ? 0 : d.idle;
          return y(idle + d.moving);
        })
        .attr('width', 36)
        // .attr('width', x.bandwidth())
        .attr('height', (d) => {
          const idle = viewMode === 'moving' ? 0 : d.idle;
          return y(idle) - y(idle + d.moving);
        })
        .attr('fill', this.color.moving)
        .style('stroke', 'none');
    }

    // globaltrend line
    const _line = line<any>()
      .x((d: any) => xLine(d.week)!)
      .y((d: any) => y(d.globaltrend));

    svg.append('path')
      .datum(data.map((d, i) => ({ week: i + '', globaltrend: d.globaltrend })))
      // .datum(data)
      .attr('fill', 'none')
      .attr('stroke', this.color.globaltrend)
      .attr('stroke-width', 2)
      .attr('d', _line);

    this.tooltipInit(data, element, svg, x, y);
  }

  private tooltipInit(data: Array<any>, element: HTMLElement, svg: Selection<SVGGElement, unknown, null, undefined>, x: ScaleBand<string>, y: ScaleLinear<number, number>): void {
    const tooltip = select(element)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('min-width', '160px')
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('border-radius', '8px')
      .style('padding', '6px 8px')
      .style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
      .style('border', '1px solid #e0e0e0')
      .style('pointer-events', 'none')
      .style('z-index', '10');


    // Transparent hover layer (for tooltip)
    svg.select('g.chart').append('g').attr('class', 'hover-area').selectAll('.tooltip-bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'tooltip-bar')
      // .attr('x', (d) => x(d.week)!)
      .attr('x', (d, i, nodes) => {
        const bandX = x(d.week)!;
        if (i === 0) {
          // align to left edge
          return bandX;
        } else if (i === data.length - 1) {
          // align to right edge by subtracting bar width
          return bandX + x.bandwidth() - 36;
        } else {
          // center in band
          const gap = (this.width - (36 * 2) - ((nodes.length - 2) * 36)) / (nodes.length - 1);
          const space = (i * 36) + (i * gap) + 18;
          return space - 18;
        }
      })
      .attr('y', 0)
      .attr('width', 36)
      // .attr('width', x.bandwidth())
      .attr('height', this.height)
      .attr('fill', 'transparent')
      .on('mouseover', (event, d) => {
        // const total = d.idle + d.moving;
        tooltip
          .html(
            `<div style="font-weight: 600; font-size: 12px; font-weight:500; color: #2B2B2B; margin-bottom: 6px;">
              ${d.week}
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <div style="width: 8px; height: 8px; background: ${this.color.moving}; border-radius: 50%; margin-right: 6px;"></div>
              <div style="color: #9FA1A4; font-size: 10px; flex: 1;">${this.transloco.translate('fuelShared.charts.movingTime')}</div>
              <div style="color: #9FA1A4; font-size: 10px;">${d.moving}hr</div>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <div style="width: 8px; height: 8px; background: ${this.color.idle}; border-radius: 50%; margin-right: 6px;"></div>
              <div style="color: #9FA1A4; font-size: 10px; flex: 1;">${this.transloco.translate('fuelShared.charts.idleTime')}</div>
              <div style="color: #9FA1A4; font-size: 10px;">${d.idle}hr</div>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <div style="width: 8px; height: 8px; background: ${this.color.globaltrend}; border-radius: 50%; margin-right: 6px;"></div>
              <div style="color: #9FA1A4; font-size: 10px; flex: 1;">${this.transloco.translate('fuelShared.charts.globalTrend')}</div>
              <div style="color: #9FA1A4; font-size: 10px;">${d.globaltrend}hr</div>
            </div>`
          )
          .style('opacity', 1);
      })
      .on('mousemove', function (event) {
        const tooltipEl = tooltip.node() as HTMLDivElement;
        const tooltipWidth = tooltipEl.offsetWidth;
        const containerRect = element.getBoundingClientRect();

        if (containerRect.right - event.clientX < tooltipWidth) {
          tooltip
            .style('left', event.offsetX - tooltipWidth - 5 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        } else {
          tooltip
            .style('left', event.offsetX + 10 + 'px')
            .style('top', event.offsetY - 28 + 'px');
        }
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0);
      });
  }
}
